//
/* GS://PROTOCOLO
 * Arquivo: src/components/pos/CustomerSearch.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 2.2.1 – FIX: Seleção de Cliente (Evento Blur)
 * Impacto da Versão: [src/components/pos/CustomerSearch.js]
 * Data: 19/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [GS-FIX]: Substituído `onClick` por `onMouseDown` nos itens da lista.
 * Motivo: O evento `onBlur` do input escondia a lista antes do `onClick` ser registrado.
 * O `onMouseDown` ocorre antes do `onBlur`, garantindo a captura da seleção.
 */
//

import React, { useState, useMemo } from 'react';
import { Search, User, X, UserPlus } from 'lucide-react';

const CustomerSearch = ({ customers, onSelectCustomer, selectedCustomer, onSelectWalkIn }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Busca inteligente com fallback seguro para nome, e-mail ou telefone
    const filteredCustomers = useMemo(() => {
        if (!searchTerm) return [];
        const lowercasedTerm = searchTerm.toLowerCase();

        return (customers || []).filter(customer =>
            (customer.name || '').toLowerCase().includes(lowercasedTerm) ||
            (customer.email || '').toLowerCase().includes(lowercasedTerm) ||
            (customer.phone || '').includes(lowercasedTerm)
        ).slice(0, 5); // Limita a 5 resultados para melhor performance
    }, [searchTerm, customers]);

    const handleSelect = (customer) => {
        onSelectCustomer(customer);
        setSearchTerm('');
        setIsFocused(false);
    };

    const clearSelection = () => {
        onSelectCustomer(null);
        setSearchTerm('');
        setIsFocused(false);
    };

    if (selectedCustomer) {
        return (
            <div className="w-full flex items-center justify-between bg-brand-gold/20 border border-brand-gold text-white p-2 rounded-lg">
                <div className="flex items-center gap-2">
                    <User size={18} className="text-brand-gold" />
                    <span className="font-bold">{selectedCustomer.name || 'Cliente Desconhecido'}</span>
                </div>
                <button onClick={clearSelection} className="p-1 text-white hover:text-red-400">
                    <X size={18} />
                </button>
            </div>
        );
    }

    return (
        <div className="relative w-full">
            <div className="flex items-center bg-gray-800 rounded-lg">
                <Search className="absolute left-3 text-gray-500" size={20} />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                    placeholder="Procurar cliente por nome, e-mail..."
                    className="w-full bg-transparent p-3 pl-10 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold"
                />
            </div>

            {isFocused && (
                <div className="absolute top-full mt-2 w-full bg-gray-800 border border-gray-700 rounded-lg z-10 shadow-lg">
                    {/* GS-FIX: onMouseDown dispara antes do onBlur do input */}
                    <div
                        onMouseDown={() => {
                            onSelectWalkIn();
                            setIsFocused(false);
                            setSearchTerm('');
                        }}
                        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-brand-gold/20 border-b border-gray-700"
                    >
                        <UserPlus size={20} className="text-brand-gold" />
                        <span className="font-semibold">Venda Avulsa (Consumidor Final)</span>
                    </div>

                    {filteredCustomers.length > 0 ? (
                        filteredCustomers.map(customer => (
                            <div
                                key={customer.id || customer.email || customer.phone}
                                // GS-FIX: onMouseDown dispara antes do onBlur do input
                                onMouseDown={() => handleSelect(customer)}
                                className="p-3 cursor-pointer hover:bg-gray-700"
                            >
                                <p className="font-semibold">{customer.name || 'Sem nome'}</p>
                                <p className="text-sm text-gray-400">
                                    {customer.email || customer.phone || 'Sem contato'}
                                </p>
                            </div>
                        ))
                    ) : (
                        searchTerm && <p className="p-3 text-gray-500">Nenhum cliente encontrado.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomerSearch;